import { useEffect, useRef } from 'react'

export type SequenceContext = {
    wait: (ms: number) => Promise<boolean>
}

type Round = (context: SequenceContext) => Promise<void>

/**
 * Plays a round of a scene again and again while `active` is true.
 * Stopping clears every pending wait, so a paused scene never updates state.
 */
export function useSequence(active: boolean, round: Round): void {
    const roundRef = useRef(round)

    useEffect(() => {
        roundRef.current = round
    })

    useEffect(() => {
        if (!active) return

        let alive = true
        const timers = new Set<number>()

        const wait = (ms: number): Promise<boolean> => new Promise(resolve => {
            const id = window.setTimeout(() => {
                timers.delete(id)
                resolve(alive)
            }, ms)
            timers.add(id)
        })

        const loop = async () => {
            if (!(await wait(250))) return

            while (alive) {
                await roundRef.current({ wait })
                if (!(await wait(16))) return
            }
        }

        void loop()

        return () => {
            alive = false
            timers.forEach(id => window.clearTimeout(id))
            timers.clear()
        }
    }, [active])
}
